import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import PackLineItem from './PackLineItem';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import { PageWrapper } from '../../../styledComponents/wrappers';
import { LpuOrder } from '../../../services/fulfillment/types';
import { confirmPickup, getLpuOrder } from '../../../services/fulfillment/lpuApi';
import { FormatDateString } from '../../../utils/helper';

const MockLpuPickupDetail: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const history = useHistory();

  const [order, setOrder] = useState<LpuOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [confirming, setConfirming] = useState(false);
  const [pickupResult, setPickupResult] = useState<{ success: boolean; error?: string } | null>(
    null,
  );

  useEffect(() => {
    const id = parseInt(orderId, 10);
    getLpuOrder(id).then(o => {
      if (!o) {
        setError('Order not found.');
        setLoading(false);
        return;
      }
      setOrder(o);
      setLoading(false);
    });
  }, [orderId]);

  if (loading || !order) {
    return (
      <PageWrapper>
        {error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        )}
      </PageWrapper>
    );
  }

  const handleConfirmPickup = async () => {
    setConfirming(true);
    setPickupResult(null);
    const result = await confirmPickup(order.shopifyOrderId);
    setConfirming(false);
    setPickupResult(result);
  };

  if (pickupResult?.success) {
    return (
      <PageWrapper>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 6, pb: 4 }}>
          <CheckCircleOutlineIcon style={{ fontSize: 80, color: '#4caf50' }} />
          <Typography variant="h5" mt={2} mb={1}>
            Pickup Confirmed!
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={1}>
            {order.shopifyOrderName}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={4}>
            {order.customer.firstName} {order.customer.lastName} &mdash; order fulfilled.
          </Typography>
          <Button variant="contained" onClick={() => history.push('/fulfillment/mock/lpu')}>
            Back to LPU Queue
          </Button>
        </Box>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5">{order.shopifyOrderName} &mdash; Confirm Pickup</Typography>
          <Typography sx={{ fontSize: 13, color: 'grey.600' }}>
            {order.customer.firstName} {order.customer.lastName}
            {order.stagedAt && <> &middot; Staged {FormatDateString(order.stagedAt)}</>}
          </Typography>
        </Box>
        <Button size="small" variant="text" onClick={() => history.push('/fulfillment/mock/lpu')}>
          &larr; LPU Queue
        </Button>
      </Box>

      <Box
        sx={{
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 1,
          mb: 3,
          overflow: 'hidden',
        }}
      >
        {order.lines.map((line, i) => (
          <Box
            key={line.shopifyLineItemGid}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              px: 2,
              py: 1.5,
              borderTop: i > 0 ? '1px solid' : 'none',
              borderColor: 'grey.200',
            }}
          >
            <PackLineItem line={line} />
          </Box>
        ))}
      </Box>

      {pickupResult && !pickupResult.success && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {pickupResult.error} &mdash; please retry.
        </Alert>
      )}

      <Button
        variant="contained"
        color="success"
        size="large"
        disabled={confirming}
        onClick={handleConfirmPickup}
        startIcon={confirming ? <CircularProgress size={16} sx={{ color: 'white' }} /> : undefined}
      >
        {confirming ? 'Confirming\u2026' : 'Confirm Pickup'}
      </Button>
    </PageWrapper>
  );
};

export default MockLpuPickupDetail;
